import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../../api';
import { upgradeAnbieten } from '../../billtoro/upgrade';
import { Dialog } from '../../components/Dialog';
import { useToast } from '../../lib/toast';
import { darf, fehlerText, TarifMarke } from '../tarifHinweis';
import { StornoDialog } from './Ausgaben';
import { JahrWahl, type SeitenProps } from './Steuer';
import { basis, centsAus, deDatum, euro, herunterladen, type Kasse, type KasseArt, type KasseBuchung, type Satz } from './daten';

type NeuArt = Exclude<KasseArt, 'ausgabe' | 'storno'>;
const ARTEN: [NeuArt, string][] = [
  ['einnahme', 'Bareinnahme (Verkauf)'],
  ['einlage', 'Privat eingelegt'],
  ['entnahme', 'Privat entnommen'],
  ['bank_ein', 'Von der Bank abgehoben'],
  ['bank_aus', 'Zur Bank gebracht'],
];

// Kassenbuch (/rechnung/steuer/kasse): für Firmen mit Bargeld. Jede Buchung mit fortlaufender Nummer, in zeitlicher
// Reihenfolge, nichts löschen (Fehler: stornieren). Bar bezahlte Ausgaben kommen von selbst aus „Ausgaben“.
export function Kassenbuch({ data, fehltAusgaben }: SeitenProps) {
  // Team-Mitglied: buchen mit „Ausgaben und Kasse“, Excel mit „Exporte“ (der Server prüft)
  const schreiben = darf(data.tarif, 'ausgaben');
  const toast = useToast();
  const [jahr, setJahr] = useState(() => Number(data.today.slice(0, 4)));
  const [k, setK] = useState<Kasse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [storno, setStorno] = useState<KasseBuchung | null>(null);

  const laden = useCallback(async () => {
    try {
      setK(await api.get<Kasse>(`${basis(data.firm.id)}/kasse?jahr=${jahr}`));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [data.firm.id, jahr]);
  useEffect(() => {
    void laden();
  }, [laden]);

  function buchen() {
    if (fehltAusgaben) upgradeAnbieten(data.tarif, 'ausgaben');
    else setNeu(true);
  }

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>Kassenbuch</h1>
          <p className="muted">Nur nötig, wenn Sie Bargeld annehmen oder bar bezahlen.</p>
        </div>
        <div className="rt-head-act">
          <JahrWahl jahr={jahr} heute={data.today} onChange={setJahr} />
          {k && k.buchungen.length > 0 && darf(data.tarif, 'export') && (
            <button
              type="button"
              className="btn"
              onClick={() => void herunterladen(`${basis(data.firm.id)}/kasse/export?jahr=${jahr}`, `Kassenbuch ${jahr}.csv`).catch((e) => toast(errorText(e)))}
            >
              Excel
            </button>
          )}
          {schreiben && (
            <button type="button" className={`btn primary${fehltAusgaben ? ' rt-gesperrt' : ''}`} disabled={fehltAusgaben === 'bald'} onClick={buchen}>
              + Buchung
              <TarifMarke tarif={data.tarif} fehlt={fehltAusgaben} />
            </button>
          )}
        </div>
      </div>
      {error && <div className="notice bad">{error}</div>}
      {!k ? (
        !error && <div className="spinner" aria-label="Lädt" />
      ) : (
        <>
          <div className="ag-kacheln">
            <div className="ag-kachel">
              <span>Bargeld in der Kasse</span>
              <b className="num">{euro(k.bestand)}</b>
            </div>
            <div className="ag-kachel">
              <span>Anfang {jahr}</span>
              <b className="num">{euro(k.anfangJahr)}</b>
            </div>
          </div>
          {k.leer ? (
            <div className="rt-card rt-empty">
              <p>Tragen Sie zuerst ein, wie viel Bargeld gerade in der Kasse ist (Anfangsbestand). Danach jede Bareinnahme und Barausgabe.</p>
              {schreiben && (
                <button type="button" className="btn primary" disabled={fehltAusgaben === 'bald'} onClick={buchen}>
                  Anfangsbestand eintragen
                </button>
              )}
            </div>
          ) : k.buchungen.length === 0 ? (
            <div className="rt-card rt-empty">
              <p>Keine Buchungen in {jahr}.</p>
            </div>
          ) : (
            <div className="ag-tabelle-rahmen">
              <table className="ag-tabelle ag-kasse">
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Datum</th>
                    <th>Was</th>
                    <th className="r">Rein</th>
                    <th className="r">Raus</th>
                    <th className="r">Bestand</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {k.buchungen.map((b) => (
                    <tr key={b.id} className={b.storniert ? 'ag-storniert' : undefined}>
                      <td className="num ag-k-nr">{b.nr}</td>
                      <td className="num ag-k-datum">{deDatum(b.datum)}</td>
                      <td className="ag-k-was">
                        <b>{b.artText}</b>
                        {b.text !== b.artText && <span className="muted"> · {b.text}</span>}
                        {b.satz != null && b.art === 'einnahme' && <span className="muted"> · {b.satz} %</span>}
                      </td>
                      <td className="r num ag-k-rein">{b.betrag > 0 ? `+${euro(b.betrag)}` : ''}</td>
                      <td className="r num ag-k-raus">{b.betrag < 0 ? `−${euro(-b.betrag)}` : ''}</td>
                      <td className="r num ag-k-bestand">{euro(b.bestand)}</td>
                      <td className="r ag-k-act">
                        {!b.storniert && b.art !== 'storno' && !b.ausgabeId && !fehltAusgaben && schreiben && (
                          <button type="button" className="linkbtn rt-danger" onClick={() => setStorno(b)}>
                            Stornieren
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="ag-fuss muted">
            Bar bezahlte Belege erfassen Sie unter <Link to="/rechnung/steuer">Ausgaben</Link> mit „Bar (Kasse)“, sie stehen dann von selbst hier.
          </p>
        </>
      )}
      {neu && k && (
        <BuchungDialog
          firmId={data.firm.id}
          kasse={k}
          kleinunternehmer={data.absender.kleinunternehmer}
          onClose={() => setNeu(false)}
          onSaved={async (nr) => {
            setNeu(false);
            toast(`Buchung ${nr} gespeichert`);
            await laden();
          }}
        />
      )}
      <StornoDialog
        titel={storno ? `Buchung ${storno.nr} stornieren?` : ''}
        text="Billtoro bucht den Betrag mit heutigem Datum zurück. Die Buchung bleibt sichtbar."
        offen={!!storno}
        onClose={() => setStorno(null)}
        onStorno={async (grund) => {
          await api.post(`${basis(data.firm.id)}/kasse/${storno!.id}/storno`, { grund });
          toast(`Buchung ${storno!.nr} storniert`);
          setStorno(null);
          await laden();
        }}
      />
    </>
  );
}

function BuchungDialog({
  firmId,
  kasse,
  kleinunternehmer,
  onClose,
  onSaved,
}: {
  firmId: string;
  kasse: Kasse;
  kleinunternehmer: boolean;
  onClose(): void;
  onSaved(nr: number): Promise<void>;
}) {
  const [art, setArt] = useState<NeuArt | 'anfang'>(kasse.leer ? 'anfang' : 'einnahme');
  const [datum, setDatum] = useState(kasse.heute);
  const [betrag, setBetrag] = useState('');
  const [satz, setSatz] = useState<Satz>(19);
  const [text, setText] = useState('');
  const [beleg, setBeleg] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const c = centsAus(betrag);
    if (c === undefined || c === null || c < 0) return setError('Bitte den Betrag prüfen, z. B. 25,00.');
    setBusy(true);
    try {
      const r = await api.post<{ nr: number }>(`${basis(firmId)}/kasse`, {
        datum,
        art,
        betragCents: c,
        satz: art === 'einnahme' ? (kleinunternehmer ? 0 : satz) : null,
        text: text.trim() || null,
        beleg: beleg.trim() || null,
      });
      await onSaved(r.nr);
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title={art === 'anfang' ? 'Anfangsbestand' : 'Kassenbuchung'} className="ag-dlg-klein">
      <form onSubmit={(e) => void speichern(e)} noValidate>
        {art !== 'anfang' && (
          <label className="field">
            <span>Was?</span>
            <select value={art} onChange={(e) => setArt(e.target.value as NeuArt)}>
              {ARTEN.map(([id, t]) => (
                <option key={id} value={id}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        )}
        {art === 'anfang' && <p className="muted">Wie viel Bargeld ist am ersten Tag in der Kasse? Danach geht es mit den einzelnen Buchungen weiter.</p>}
        <div className="ag-grid">
          <label className="field">
            <span>Datum</span>
            <input type="date" value={datum} min={kasse.letztesDatum ?? undefined} max={kasse.heute} onChange={(e) => setDatum(e.target.value)} />
          </label>
          <label className="field">
            <span>Betrag</span>
            <input value={betrag} inputMode="decimal" placeholder="0,00" onChange={(e) => setBetrag(e.target.value)} />
          </label>
        </div>
        {art === 'einnahme' && !kleinunternehmer && (
          <label className="field">
            <span>Steuersatz</span>
            <select value={String(satz)} onChange={(e) => setSatz(Number(e.target.value) as Satz)}>
              <option value="19">19 %</option>
              <option value="7">7 %</option>
              <option value="0">0 %</option>
            </select>
          </label>
        )}
        {art !== 'anfang' && (
          <div className="ag-grid">
            <label className="field">
              <span>Text (optional)</span>
              <input value={text} maxLength={200} placeholder="z. B. Tageseinnahmen" onChange={(e) => setText(e.target.value)} />
            </label>
            <label className="field">
              <span>Beleg-Nr. (optional)</span>
              <input value={beleg} maxLength={60} onChange={(e) => setBeleg(e.target.value)} />
            </label>
          </div>
        )}
        {kasse.letztesDatum && <small className="muted">Das Kassenbuch läuft der Reihe nach: Datum ab {deDatum(kasse.letztesDatum)}.</small>}
        {error && <div className="notice bad">{error}</div>}
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            Buchen
          </button>
        </div>
      </form>
    </Dialog>
  );
}
